import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { ArrowLeft, Send, Loader2, Hexagon, MessageCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/lib/api";

const pub = axios.create({ baseURL: API });

const STATUS_THEME = {
  open:        "bg-amber-100 text-amber-900",
  in_progress: "bg-blue-100 text-blue-900",
  resolved:    "bg-emerald-100 text-emerald-900",
  spam:        "bg-stone-200 text-stone-700",
};

export default function PublicSupportReply() {
  const { token } = useParams();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sentOnce, setSentOnce] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await pub.get(`/public/support/reply/${token}`);
      setThread(r.data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error("Couldn't load this thread.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const send = async () => {
    if (message.trim().length < 2) {
      toast.error("Reply is too short");
      return;
    }
    setSending(true);
    try {
      await pub.post(`/public/support/reply/${token}`, { message });
      toast.success("Reply sent — we'll get back to you soon.");
      setMessage("");
      setSentOnce(true);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="public-support-reply-page">
      <header className="max-w-3xl mx-auto flex items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-2" data-testid="back-to-landing">
          <div className="w-8 h-8 rounded-lg bg-yellow-400 grid place-items-center">
            <Hexagon className="w-5 h-5 text-amber-900" strokeWidth={2.5} />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight">Hive <span className="text-yellow-500">@</span> Buzzzillion</span>
        </Link>
        <Link to="/" className="text-xs text-stone-500 hover:text-stone-800 inline-flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> Home
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-16">
        {loading && <div className="text-sm text-stone-500 py-10 text-center" data-testid="thread-loading">Loading…</div>}
        {notFound && (
          <div className="bg-white border border-stone-200 rounded-2xl p-8 text-center" data-testid="thread-not-found">
            <h1 className="font-display text-2xl font-medium mb-2">Thread not found</h1>
            <p className="text-sm text-stone-500">This reply link is invalid or has expired. Send us a fresh message from the homepage.</p>
          </div>
        )}
        {thread && (
          <div className="space-y-6" data-testid="thread">
            <div className="bg-white border border-stone-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-0.5 rounded-full ${STATUS_THEME[thread.status] || STATUS_THEME.open}`} data-testid="thread-status">
                  {thread.status.replace("_", " ")}
                </span>
                <span className="text-xs text-stone-500">{format(parseISO(thread.created_at), "MMM d, yyyy · h:mm a")}</span>
              </div>
              <h1 className="font-display text-2xl font-bold tracking-tight text-stone-900" data-testid="thread-subject">{thread.subject}</h1>
              <p className="text-xs text-stone-500 mt-1">From <span className="font-medium text-stone-700">{thread.name}</span> · {thread.email}</p>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mt-4 text-sm whitespace-pre-wrap text-stone-800 leading-relaxed" data-testid="thread-original">
                {thread.message}
              </div>
            </div>

            {thread.conversation.length > 0 && (
              <ol className="space-y-3" data-testid="thread-conversation">
                {thread.conversation.map((r) => (
                  <li
                    key={r.id}
                    className={`rounded-2xl border p-4 ${r.from === "admin" ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200 ml-8"}`}
                    data-testid={`conv-${r.from}-${r.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`text-[10px] uppercase tracking-[0.14em] font-bold ${r.from === "admin" ? "text-amber-800" : "text-blue-800"}`}>
                        {r.from === "admin" ? "Support team" : "You"}
                      </span>
                      <span className="text-[11px] text-stone-500">{format(parseISO(r.sent_at), "MMM d · h:mm a")}</span>
                    </div>
                    <div className="text-sm whitespace-pre-wrap text-stone-800 leading-relaxed">{r.body}</div>
                  </li>
                ))}
              </ol>
            )}

            <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="reply-composer">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="w-4 h-4 text-stone-500" />
                <h2 className="font-display text-lg font-medium">Add a follow-up</h2>
                {sentOnce && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-800 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full">
                    <CheckCircle2 className="w-3 h-3" /> Delivered
                  </span>
                )}
              </div>
              <Textarea
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Write your reply here…"
                className="text-sm"
                data-testid="reply-body-input"
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-[11px] text-stone-400">{message.length}/4000</span>
                <Button
                  onClick={send}
                  disabled={sending || message.trim().length < 2}
                  className="rounded-full bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold"
                  data-testid="reply-send-button"
                >
                  {sending ? (<><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending…</>) : (<><Send className="w-3.5 h-3.5 mr-1.5" /> Send reply</>)}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
